import React from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateY(-50px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;

  .header-content {
    flex: 1;
  }

  h2 {
    margin: 0 0 0.5rem 0;
    font-size: 1.8rem;
    font-weight: 700;
  }

  p {
    margin: 0;
    opacity: 0.9;
    font-size: 0.95rem;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
`;

const ModalBody = styled.div`
  padding: 2rem;

  .detail-section {
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .section-title {
    font-size: 1rem;
    font-weight: 700;
    color: #667eea;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #e0e0e0;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f0f0f0;

    &:last-child {
      border-bottom: none;
    }

    label {
      font-weight: 600;
      color: #333;
      min-width: 150px;
    }

    value {
      color: #555;
      text-align: right;
      flex: 1;
    }

    .status-badge {
      padding: 0.4rem 1rem;
      border-radius: 20px;
      font-size: 0.85rem;
      font-weight: 700;
      color: white;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      background: ${props => {
        if (props.status === 'Approved') return '#4caf50';
        if (props.status === 'Rejected') return '#f44336';
        return '#ff9800';
      }};
    }
  }

  .status-info {
    padding: 1rem;
    border-radius: 8px;
    margin-top: 0.5rem;
    font-size: 0.9rem;

    &.approved {
      background: #e8f5e9;
      color: #2e7d32;
    }

    &.pending {
      background: #fff3e0;
      color: #e65100;
    }

    &.rejected {
      background: #ffebee;
      color: #c62828;
    }
  }

  a {
    color: #667eea;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ListingDetailsModal = ({ listing, onClose }) => {
  if (!listing) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div className="header-content">
            <h2>🌾 {listing.cropType}</h2>
            <p>{listing.breedVariety || 'Standard Variety'}</p>
          </div>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          {/* Crop Information */}
          <div className="detail-section">
            <div className="section-title">Crop Information</div>
            <div className="detail-row">
              <label>Crop Type:</label>
              <value>{listing.cropType}</value>
            </div>
            <div className="detail-row">
              <label>Variety:</label>
              <value>{listing.breedVariety || 'Not specified'}</value>
            </div>
            {listing.farmingDetails && (
              <div className="detail-row">
                <label>Farming Details:</label>
                <value>{listing.farmingDetails}</value>
              </div>
            )}
          </div>

          {/* Location Information */}
          <div className="detail-section">
            <div className="section-title">Location</div>
            <div className="detail-row">
              <label>State:</label>
              <value>{listing.state}</value>
            </div>
            <div className="detail-row">
              <label>Region:</label>
              <value>{listing.region}</value>
            </div>
            <div className="detail-row">
              <label>Cultivation Area:</label>
              <value>{listing.cultivationAreaInAcres} acres</value>
            </div>
          </div>

          {/* Production Details */}
          <div className="detail-section">
            <div className="section-title">Production & Pricing</div>
            <div className="detail-row">
              <label>Expected Harvest:</label>
              <value>{listing.harvestQuantityInKg.toLocaleString()} kg</value>
            </div>
            <div className="detail-row">
              <label>Minimum Order:</label>
              <value>{listing.minimumOrderQuantity.toLocaleString()} kg</value>
            </div>
            <div className="detail-row">
              <label>Base Price:</label>
              <value>₹{listing.basePrice}/kg</value>
            </div>
          </div>

          {/* Status */}
          <div className="detail-section">
            <div className="section-title">Status</div>
            <div className="detail-row">
              <label>Status:</label>
              <span className="status-badge">{listing.status}</span>
            </div>

            {listing.status === 'Approved' && listing.approvedAt && (
              <div className="status-info approved">
                ✓ Approved on {new Date(listing.approvedAt).toLocaleDateString()}
              </div>
            )}
            {listing.status === 'Pending' && (
              <div className="status-info pending">
                ⏳ Your listing is under review by our admin team
              </div>
            )}
            {listing.status === 'Rejected' && listing.rejectionReason && (
              <div className="status-info rejected">
                Rejection Reason: {listing.rejectionReason}
              </div>
            )}
          </div>

          {/* Certification */}
          {listing.certificationUrl && (
            <div className="detail-section">
              <div className="section-title">Certification</div>
              <div className="detail-row">
                <label>Certificate:</label>
                <a href={listing.certificationUrl} target="_blank" rel="noopener noreferrer">
                  View Document ↗
                </a>
              </div>
            </div>
          )}

          {/* Created Date */}
          <div className="detail-section">
            <div className="section-title">Timeline</div>
            <div className="detail-row">
              <label>Created:</label>
              <value>{new Date(listing.createdAt).toLocaleDateString()}</value>
            </div>
          </div>
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
};

export default ListingDetailsModal;
